import { useCallback } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useTelegramWebApp } from './useTelegramWebApp';
import { fetchCustomerCredential, type CustomerCredential } from '../services/customerTokenApi';

/**
 * La credencial con la que esta persona consulta SUS reservas, reuniendo por su cuenta todo lo
 * que el navegador sabe de quién es.
 *
 * ⚠️ EXISTE PARA QUE NO SE PUEDA VOLVER A OLVIDAR NADA. `fetchCustomerCredential` acepta el
 * token de Clerk y el `initData` de Telegram como parámetros OPCIONALES, así que llamarla mal
 * —`{ tenantId }` a secas— compila, funciona y devuelve una credencial que solo acredita al
 * invitado de ese navegador. Tres pantallas lo hacían así.
 *
 * Mientras booking-service aceptaba peticiones sin credencial no se notaba: contestaba igual.
 * Al cerrar esa puerta el 5-sep-2026, la omisión se convirtió en «entro con mi cuenta desde
 * otro dispositivo y veo un error en mis propias reservas». El arreglo no fue pasar los
 * argumentos en los tres sitios: fue que no haya argumentos que pasar.
 *
 * Devuelve una función y no el valor porque los tres usos son distintos —uno al montar, otro
 * al abrir un panel, otro dentro de una promesa encadenada— y un hook que dispara solo
 * obligaría a los dos últimos a pedirla antes de necesitarla.
 */
export function useCustomerCredential(): (tenantId: string) => Promise<CustomerCredential> {
  const { getToken } = useCustomerAuth();
  const { initData: telegramInitData } = useTelegramWebApp();

  return useCallback(
    async (tenantId: string) => {
      /**
       * Un fallo pidiendo el token de sesión no puede impedir pedir la credencial: quedan el
       * `initData` de Telegram y el `guestId`, que para mucha gente son la identidad buena.
       */
      const clerkToken = await getToken().catch(() => null);
      return fetchCustomerCredential({
        tenantId,
        clerkToken,
        telegramInitData: telegramInitData || null,
      });
    },
    [getToken, telegramInitData],
  );
}
